import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { Modal, message } from 'antd';
import moment from 'moment';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { PageHeader } from 'components/page/page-header';
import { ApiIcon, CodeIcon, DocsIcon, ExternalIcon } from 'components/ob-icons';

import { createDevToken, fetchDevTokens, revokeDevToken } from './api';
import { ApiDocs } from './ApiDocs';
import { API_DOCS_URL } from '../../config/constants';
import { DevToken } from '../../types/dev-token.types';

const RESOURCE_LINKS = [
  { href: 'https://github.com/the1browser/1browser', label: 'Browser Automation Docs', icon: DocsIcon },
  { href: 'https://github.com/the1browser/1browser-devtools-mcp', label: '1Browser MCP', icon: CodeIcon },
  { href: 'https://www.npmjs.com/package/@the1browser/onebrowser-mcp', label: 'MCP npm package', icon: CodeIcon },
];

const SectionTitle: FC<{ title: string; hint?: string }> = ({ title, hint }) => (
  <div>
    <h2 className='text-lg font-medium text-ob-text'>{title}</h2>
    {hint && <p className='mt-2 text-ob-text-subtle'>{hint}</p>}
  </div>
);

export const ApiPage: FC = () => {
  const { t: translation } = useTranslation();
  const [tokens, setTokens] = useState<DevToken[]>([]);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [revokingTokenId, setRevokingTokenId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const [revokeTargetId, setRevokeTargetId] = useState<string | null>(null);

  const loadTokens = useCallback(async () => {
    try {
      const data = await fetchDevTokens();
      setTokens(data);
    } catch (error) {
      console.error('Failed to fetch dev tokens:', error);
      message.error(translation('apiPage.failed to load tokens'));
    } finally {
      setIsLoading(false);
    }
  }, [translation]);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const result = await createDevToken();
      setNewToken(result.token);
      setIsCopied(false);
      await loadTokens();
      message.success(translation('apiPage.dev token generated'));
    } catch (error) {
      console.error('Failed to create dev token:', error);
      message.error(translation('apiPage.failed to generate token'));
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);

      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  const handleCopy = async () => {
    if (!newToken) {
      return;
    }

    try {
      await copyToClipboard(newToken);
      setIsCopied(true);
      message.success(translation('apiPage.token copied'));
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy token:', error);
      message.error(translation('apiPage.failed to copy token'));
    }
  };

  const handleRevoke = (tokenId: string) => {
    setRevokeTargetId(tokenId);
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTargetId) {
      return;
    }

    setRevokingTokenId(revokeTargetId);
    try {
      await revokeDevToken(revokeTargetId);
      setTokens((prev) => prev.filter((token) => token.tokenId !== revokeTargetId));
      setRevokeTargetId(null);
      message.success(translation('apiPage.token revoked'));
    } catch (error) {
      console.error('Failed to revoke dev token:', error);
      message.error(translation('apiPage.failed to revoke token'));
    } finally {
      setRevokingTokenId(null);
    }
  };

  const formatDate = (date: string) => moment(date).format('MMM D, YYYY HH:mm');

  const resourceLinks = [
    ...RESOURCE_LINKS,
    { href: API_DOCS_URL, label: translation('apiPage.open docs'), icon: ApiIcon },
  ];

  return (
    <div className='mx-auto w-[calc(100%-32px)] py-8 md:w-[min(1120px,calc(100%-64px))] md:pb-16 md:pt-10'>
      <div className='flex flex-wrap gap-2.5'>
        {resourceLinks.map(({ href, label, icon: Icon }) => (
          <a
            key={href}
            href={href}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex min-h-11 items-center gap-2.5 rounded-[10px] border border-ob-border bg-ob-raised px-4 font-medium text-ob-text-secondary transition-colors hover:border-ob-border-hover hover:bg-ob-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ob-focus'
          >
            <Icon className='h-4 w-4 shrink-0 text-ob-text-muted' />
            <span>{label}</span>
            <ExternalIcon className='ml-0.5 h-4 w-4 shrink-0 text-ob-text-muted' />
          </a>
        ))}
      </div>

      <div className='mt-10'>
        <PageHeader
          icon={ApiIcon}
          title={translation('apiPage.title')}
          description={translation('apiPage.description')}
        />
      </div>

      <section className='mt-[34px] space-y-4'>
        <div className='flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center sm:gap-6'>
          <SectionTitle title={translation('apiPage.dev tokens')} hint={translation('apiPage.dev tokens hint')} />
          <Button variant='ob-primary' size='pill' className='shrink-0' onClick={handleGenerate} disabled={isGenerating}>
            {translation('apiPage.generate dev token')}
          </Button>
        </div>

        {newToken ? (
          <div className='space-y-4 rounded-[14px] border border-ob-warning/40 bg-ob-warning/10 p-5'>
            <div className='flex items-start gap-3 text-ob-text-secondary'>
              <AlertTriangle size={20} className='mt-0.5 shrink-0 text-ob-warning' />
              <p className='text-sm'>{translation('apiPage.token shown once warning')}</p>
            </div>
            <div className='flex flex-col gap-2 sm:flex-row'>
              <Input
                readOnly
                value={newToken}
                className='h-11 min-w-0 rounded-[10px] border-ob-border bg-ob-shell font-mono text-xs text-ob-text-secondary shadow-none focus-visible:border-ob-primary focus-visible:ring-0'
              />
              <Button
                type='button'
                variant='ob-outline'
                size='pill'
                className='shrink-0 transition-transform active:scale-95'
                onClick={handleCopy}
              >
                {isCopied ? <Check size={16} /> : <Copy size={16} />}
                {isCopied ? translation('apiPage.copied') : translation('apiPage.copy')}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <section className='mt-[34px]'>
        <SectionTitle title={translation('apiPage.active tokens')} />

        {isLoading ? (
          <p className='mt-2 text-ob-text-subtle'>{translation('apiPage.loading')}</p>
        ) : tokens.length === 0 ? (
          <p className='mt-2 text-ob-text-subtle'>{translation('apiPage.no tokens')}</p>
        ) : (
          <div className='mt-3.5 grid gap-2.5'>
            {tokens.map((token) => (
              <div
                key={token.tokenId}
                className='flex items-center justify-between gap-5 rounded-[10px] bg-ob-raised px-4 py-3.5'
              >
                <div className='grid min-w-0 gap-[5px]'>
                  <strong className='truncate font-mono text-sm font-medium text-ob-text-secondary'>{token.tokenId}</strong>
                  <span className='text-xs text-ob-text-muted'>
                    {translation('apiPage.created at')}: {formatDate(token.createdAt)}
                    {' · '}
                    {translation('apiPage.expires at')}: {formatDate(token.expiresAt)}
                  </span>
                </div>
                <Button
                  type='button'
                  variant='ob-outline'
                  size='pill-xs'
                  className='shrink-0 text-ob-danger-text'
                  disabled={revokingTokenId === token.tokenId}
                  onClick={() => handleRevoke(token.tokenId)}
                >
                  {translation('apiPage.revoke')}
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className='mt-10'>
        <SectionTitle
          title={translation('apiPage.api documentation')}
          hint={translation('apiPage.api documentation hint')}
        />
        <div className='mt-[18px]'>
          <ApiDocs authToken={newToken} />
        </div>
      </section>

      <Modal
        open={!!revokeTargetId}
        title={translation('apiPage.revoke token')}
        okText={translation('apiPage.revoke')}
        cancelText={translation('base.cancel')}
        okButtonProps={{ danger: true }}
        confirmLoading={!!revokingTokenId}
        onOk={handleConfirmRevoke}
        onCancel={() => setRevokeTargetId(null)}
      >
        <p>{translation('apiPage.revoke token confirmation')}</p>
      </Modal>
    </div>
  );
};
